"use client";

import { useSession } from "next-auth/react";
import HeaderWrapper from "./HeaderWrapper";
import MiddleComponent from "./MiddleComponent";
import RightContainer from "./RightContainer";
import LeftContainer from "./LeftContainer";

const Header: React.FC = () => {
  const { data: session, status } = useSession();
  const isLoggedIn = Boolean(session?.user);
  const user = session?.user;

  // `(app)` layouts hand SessionProvider a server-fetched session, so status is
  // already resolved on first render. `(public)` layouts deliberately don't (it
  // would opt every SEO page out of static rendering), so status starts as
  // "loading" until the client fetches /api/auth/session. Rendering the
  // logged-out header during that window flashes a "Login" button at users who
  // are in fact signed in.
  //
  // Only the right slot's Login branch is gated on this. MiddleComponent is
  // left alone deliberately: it renders nothing for an unknown user either way,
  // so the nav stays empty for the window and then fills in — a pop-in, not a
  // wrong state. There is no correct nav to draw before auth resolves.
  const isAuthPending = status === "loading";

  return (
    <HeaderWrapper className="fixed top-[var(--status-banner-height,0px)] z-50 flex w-full items-center gap-4 px-4 text-muted-foreground lg:px-8">
      <div className="flex flex-shrink-0 basis-[45px] items-center justify-start md:basis-[100px] lg:basis-[200px]">
        <LeftContainer logoSrcLinkPath="/" />
      </div>
      <div className="flex min-w-0 flex-1 justify-center">
        <MiddleComponent isLoggedIn={isLoggedIn} />
      </div>
      <div
        // No fixed/shrink-0 basis below `md` (unlike the left column): the
        // centered `MiddleComponent` nav never renders below `md` for any
        // user (see its own `!isLessThanMd` gate), so there's nothing to
        // stay symmetric with at mobile widths — this column can size to
        // its content there instead. That's needed once both the Login
        // button and the app-store badge render side by side: a pinned
        // basis-[45px] + shrink-0 doesn't grow to fit them, so the second
        // element renders past the box's own edge and can end up clipped
        // off the viewport. `md:`/`lg:` keep the original fixed, symmetric
        // basis so the desktop centered nav still centers correctly.
        className="flex justify-end md:flex-shrink-0 md:basis-[100px] lg:basis-[200px]"
      >
        <RightContainer
          user={user}
          isLoggedIn={isLoggedIn}
          isAuthPending={isAuthPending}
        />
      </div>
    </HeaderWrapper>
  );
};

export default Header;
